/**
 * Backend analytics hook — writes funnel events to our own D1 analytics_events table
 * in addition to GA4. Uses tRPC analytics.track mutation.
 *
 * Only fires when user is authenticated (authedQuery). For anonymous users,
 * events are captured by GA4 only.
 */
import { useCallback } from 'react';
import { trpc } from '@/providers/trpc';

export function useBackendAnalytics() {
  const track = trpc.analytics.track.useMutation();

  const trackEvent = useCallback(
    (
      event: string,
      category: string,
      properties?: Record<string, unknown>
    ) => {
      (track.mutate as any)({
        event,
        category,
        properties: properties ? JSON.stringify(properties) : undefined,
      });
    },
    [track]
  );

  const trackPageView = useCallback(
    (path: string, title?: string) => {
      (track.mutate as any)({
        event: 'page_view',
        category: 'engagement',
        properties: JSON.stringify({ path, title: title || document.title }),
      });
    },
    [track]
  );

  const trackSearch = useCallback(
    (query: string, resultCount: number, filters?: Record<string, unknown>) => {
      (track.mutate as any)({
        event: 'parcel_search',
        category: 'search',
        properties: JSON.stringify({ query, resultCount, ...filters }),
      });
    },
    [track]
  );

  const trackSave = useCallback(
    (parcelId: number, action: 'save' | 'unsave') => {
      (track.mutate as any)({
        event: `parcel_${action}`,
        category: 'engagement',
        properties: JSON.stringify({ parcelId }),
      });
    },
    [track]
  );

  const trackTicket = useCallback(
    (subject: string, category: string) => {
      (track.mutate as any)({
        event: 'ticket_create',
        category: 'support',
        properties: JSON.stringify({ subject, category }),
      });
    },
    [track]
  );

  const trackZeroResult = useCallback(
    (query: string, county: string, state: string) => {
      (track.mutate as any)({
        event: 'zero_result_search',
        category: 'search',
        properties: JSON.stringify({ query, county, state }),
      });
    },
    [track]
  );

  return {
    trackEvent,
    trackPageView,
    trackSearch,
    trackSave,
    trackTicket,
    trackZeroResult,
  };
}
